/**
 * Data Share — which share links we accept, and nothing else.
 *
 * The server never fetches the URL a visitor typed. It pulls the share ID out of it here, checks
 * the ID's shape, and then builds the provider's own address from that ID. So a link can only ever
 * lead to one of the three providers' public share endpoints; anything else is refused before a
 * single request is made.
 *
 * The one exception is Gemini's short link (share.gemini.google/<code>, g.co/gemini/share/<code>):
 * its real ID is only known after one redirect. The server follows that redirect by hand, without
 * auto-follow, and the Location must itself parse as a gemini.google.com share link here.
 */

import type { ShareProvider } from "./types";

export type ParsedShareLink =
  | { ok: true; provider: ShareProvider; kind: "share"; id: string }
  | { ok: true; provider: "gemini"; kind: "short"; code: string; shortUrl: string }
  | { ok: false; provider?: ShareProvider; reason: string };

const CLAUDE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** ChatGPT share IDs are UUID-shaped but not always RFC-4122 (6aeb7c30-7098-832b-...). */
const CHATGPT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GEMINI_ID = /^[0-9a-zA-Z_-]{6,64}$/;
const SHORT_CODE = /^[0-9a-zA-Z_-]{4,64}$/;
/** DeepSeek and Kimi share ids: lowercase letters and digits (dh5nbjrhk7i13sclb4, dbloikdkjfligs74qjg0). */
const SHORT_ID = /^[0-9a-zA-Z_-]{8,64}$/;
/** Grok share ids: "<base64 shard>_<uuid>". */
const GROK_ID = /^[A-Za-z0-9-]{2,40}_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const PROVIDER_LABEL: Record<ShareProvider, string> = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  deepseek: "DeepSeek",
  kimi: "Kimi",
  zai: "Z.ai",
  mistral: "Mistral",
  perplexity: "Perplexity",
  grok: "Grok",
};

/** The domain whose favicon stands for each provider, fetched through /api/favicon (never by the browser). */
export const PROVIDER_DOMAIN: Record<ShareProvider, string> = {
  chatgpt: "chatgpt.com",
  claude: "claude.ai",
  gemini: "gemini.google.com",
  deepseek: "chat.deepseek.com",
  kimi: "kimi.ai",
  zai: "z.ai",
  mistral: "mistral.ai",
  perplexity: "perplexity.ai",
  grok: "grok.com",
};

function toUrl(raw: string): URL | null {
  let s = (raw || "").trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  try {
    const u = new URL(s);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    if (u.username || u.password || (u.port && u.port !== "443" && u.port !== "80")) return null;
    return u;
  } catch {
    return null;
  }
}

export function parseShareLink(raw: string): ParsedShareLink {
  // Kimi's Share button copies a sentence with the link at the end ("Click the link to view the
  // conversation with Kimi https://www.kimi.ai/share/..."). Take the first URL out of any pasted text.
  const found = /https?:\/\/[^\s<>"']+/i.exec(raw || "");
  const u = toUrl(found ? found[0] : raw);
  if (!u) return { ok: false, reason: "That doesn't look like a link." };
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  const parts = u.pathname.split("/").filter(Boolean);

  // ── Claude ── claude.ai/share/<uuid>
  if (host === "claude.ai") {
    if (parts[0] === "share" && parts[1] && CLAUDE_ID.test(parts[1])) {
      return { ok: true, provider: "claude", kind: "share", id: parts[1].toLowerCase() };
    }
    return {
      ok: false,
      provider: "claude",
      reason:
        "That's a private Claude link. Open the chat, choose Share, create a public link and paste that link (it starts claude.ai/share/).",
    };
  }

  // ── ChatGPT ── chatgpt.com/share/<id>, also chat.openai.com and /share/e/<id>
  if (host === "chatgpt.com" || host === "chat.openai.com") {
    const at = parts[0] === "share" ? (parts[1] === "e" ? 2 : 1) : -1;
    if (at > 0 && parts[at] && CHATGPT_ID.test(parts[at])) {
      return { ok: true, provider: "chatgpt", kind: "share", id: parts[at].toLowerCase() };
    }
    if (parts[0] === "g" && parts.includes("project")) {
      return {
        ok: false,
        provider: "chatgpt",
        reason:
          "ChatGPT projects can't be shared publicly: a project link only opens for its owner. Share the chats in it one by one, or upload your ChatGPT data export, which includes every chat in every project.",
      };
    }
    if (parts[0] === "c") {
      return {
        ok: false,
        provider: "chatgpt",
        reason:
          "That's a private ChatGPT link. Open the chat, choose Share, create a link and paste that one (it starts chatgpt.com/share/).",
      };
    }
    return { ok: false, provider: "chatgpt", reason: "Paste a ChatGPT share link (chatgpt.com/share/...)." };
  }

  // ── Gemini ── gemini.google.com/share/<id>, plus the short forms
  if (host === "gemini.google.com" || host === "bard.google.com") {
    if (parts[0] === "share" && parts[1] && GEMINI_ID.test(parts[1])) {
      return { ok: true, provider: "gemini", kind: "share", id: parts[1] };
    }
    return {
      ok: false,
      provider: "gemini",
      reason:
        "That's a private Gemini link. Open the chat, choose Share, create a public link and paste that one.",
    };
  }
  if (host === "share.gemini.google" && parts.length === 1 && SHORT_CODE.test(parts[0])) {
    return {
      ok: true,
      provider: "gemini",
      kind: "short",
      code: parts[0],
      shortUrl: `https://share.gemini.google/${parts[0]}`,
    };
  }
  if (host === "g.co" && parts[0] === "gemini" && parts[1] === "share" && parts[2] && SHORT_CODE.test(parts[2])) {
    return {
      ok: true,
      provider: "gemini",
      kind: "short",
      code: parts[2],
      shortUrl: `https://g.co/gemini/share/${parts[2]}`,
    };
  }

  // ── DeepSeek ── chat.deepseek.com/share/<id>
  if (host === "chat.deepseek.com") {
    if (parts[0] === "share" && parts[1] && SHORT_ID.test(parts[1])) {
      return { ok: true, provider: "deepseek", kind: "share", id: parts[1] };
    }
    return {
      ok: false,
      provider: "deepseek",
      reason: "That's a private DeepSeek link. Open the chat, press Share, create a link and paste that one (it starts chat.deepseek.com/share/).",
    };
  }

  // ── Kimi ── kimi.ai/share/<id> (and kimi.com, the older domain)
  if (host === "kimi.ai" || host === "kimi.com") {
    if (parts[0] === "share" && parts[1] && SHORT_ID.test(parts[1])) {
      return { ok: true, provider: "kimi", kind: "share", id: parts[1] };
    }
    return {
      ok: false,
      provider: "kimi",
      reason: "That's a private Kimi link. Open the chat, press Share, copy the link and paste it here (it contains kimi.ai/share/).",
    };
  }

  // ── Z.ai ── chat.z.ai/s/<uuid>. Recognised, not yet readable (checked 2026-11-05): the share
  // endpoint answers 403 without a Z.ai session, and most of an agent chat's messages load from
  // further calls behind the same session. We do not open sessions on a provider's service.
  if (host === "chat.z.ai" || host === "z.ai") {
    return {
      ok: false,
      provider: "zai",
      reason: "Z.ai only shows shared chats inside its own site. Open the link in your browser and click the askFinz button (below) to bring the chat here.",
    };
  }

  // ── Mistral (Le Chat / Vibe) ── chat.mistral.ai/work/<uuid> is a shared chat, readable without
  // signing in. The page is behind Cloudflare, which sometimes shows its browser check to a server;
  // fetch-share.ts reports that as "try again", it never tries to pass it.
  if (host === "chat.mistral.ai") {
    if (parts[0] === "work" && parts[1] && CLAUDE_ID.test(parts[1])) {
      return { ok: true, provider: "mistral", kind: "share", id: parts[1].toLowerCase() };
    }
    return {
      ok: false,
      provider: "mistral",
      reason: "That's a private Mistral link. Open the chat, press Share, make it public and paste that link (it contains chat.mistral.ai/work/).",
    };
  }

  // ── Grok ── grok.com/share/<shard>_<uuid> (the first part is base64 for e.g. "shard-2").
  if (host === "grok.com") {
    if (parts[0] === "share" && parts[1] && GROK_ID.test(parts[1])) {
      return { ok: true, provider: "grok", kind: "share", id: parts[1] };
    }
    return {
      ok: false,
      provider: "grok",
      reason: "That's a private Grok link. Open the chat, press Share, create a link and paste that one (it starts grok.com/share/).",
    };
  }

  // ── Perplexity ── perplexity.ai/search/<slug> (and /page/<slug>). Recognised, not yet readable
  // (checked 2026-11-05): both the page and its /rest/thread data endpoint answer a server with a
  // Cloudflare browser check (403 "Just a moment..."). Same rule as Mistral: no working around it.
  if (host === "perplexity.ai") {
    return {
      ok: false,
      provider: "perplexity",
      reason: "Perplexity only shows shared threads to a browser. Open the link and click the askFinz button (below) to bring the thread here.",
    };
  }

  return {
    ok: false,
    reason: "We accept public share links from ChatGPT, Claude, Gemini, DeepSeek, Kimi, Mistral, Grok, Perplexity and Z.ai.",
  };
}

/** The public page a person can open to see the original. Display only. */
export function publicShareUrl(provider: ShareProvider, id: string): string {
  if (provider === "claude") return `https://claude.ai/share/${id}`;
  if (provider === "chatgpt") return `https://chatgpt.com/share/${id}`;
  if (provider === "deepseek") return `https://chat.deepseek.com/share/${id}`;
  if (provider === "kimi") return `https://www.kimi.ai/share/${id}`;
  if (provider === "zai") return `https://chat.z.ai/s/${id}`;
  if (provider === "mistral") return `https://chat.mistral.ai/work/${id}`;
  if (provider === "perplexity") return `https://www.perplexity.ai/search/${id}`;
  if (provider === "grok") return `https://grok.com/share/${id}`;
  return `https://gemini.google.com/share/${id}`;
}
